import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Users, Check, Loader2, UserPlus } from 'lucide-react';
import { getFileURL } from '../../utils/fileURL';
import { hospitalPatientCode } from '../../utils/patientCode';

// ONE CONTACT, SEVERAL PATIENTS.
//
// Anitha's e-mail is also her three children's login: they are secondary rows
// on her household, reached by switching profile from her account. Until now
// the desk forms asked "does this contact exist here?", got a yes, and quietly
// reused THE one row they were handed — which booked the mother when the visit
// was for her son, and gave no hint that anything had been chosen.
//
// So this is the card that turns that single silent answer into a question:
// who, of this household, is this for? The primary first, then each member,
// then — when the server says the household may still grow — a last row that
// adds a brand-new member. No contact fields in that form on purpose: the
// contact is Anitha's, and a member never has one of their own.
//
// It is shared by the booking modal and Add patient because the QUESTION is
// the same in both; what differs is the answer's consequence (book for them /
// open their profile), which is why picking only calls back and the footer is
// the caller's.
//
// Keys live under a caller-supplied prefix — `appointmentmodal.household.*` or
// `addPatient.household.*` — so the two screens can word the same row
// differently without this component knowing which one it is in.

// Mirrors Backend/services/familyAccountService.js RELATIONSHIPS. The member
// endpoint REFUSES anything outside this list, so it is a closed picker rather
// than a free-text field — and the labels are the family screens' own
// `family.rel.*` strings, so the desk and the patient read the same words.
// Used only when the caller has no server-supplied list to hand in.
const HOUSEHOLD_RELATIONSHIPS = [
    'Son', 'Daughter', 'Spouse', 'Father', 'Mother',
    'Brother', 'Sister', 'Grandparent', 'Grandchild',
    'Nephew', 'Niece', 'Friend', 'Guardian', 'Other',
];

// Mirrors CONSENT_REQUIRED in the same service. NOT an age rule: these are the
// relations that are another adult, and one adult is about to be able to read
// another adult's medical record — so the server wants evidence that permission
// was actually asked for, and refuses the create without it.
const CONSENT_REQUIRED = new Set(['Spouse', 'Father', 'Mother', 'Friend', 'Guardian', 'Other']);

const ADD_NEW_MEMBER = '__add_new_member__';

// Local, not shared with the modals' own helpers: this component is handed
// plain rows and must not depend on either form's state.
const fullName = (p) => [p?.name, p?.surname].filter(Boolean).join(' ').trim();

const initialsOf = (p) => {
    const n = (p?.name || '').trim();
    const s = (p?.surname || '').trim();
    const letters = `${n.charAt(0)}${s.charAt(0)}`.trim();
    return (letters || '?').toUpperCase();
};

// UTC getters on the birth date, local ones on today — the same mix every
// other form in this app uses, so a DOB never shifts a day across time zones.
const ageFromDob = (dob) => {
    if (!dob) return null;
    const birth = new Date(dob);
    if (Number.isNaN(birth.getTime())) return null;
    const today = new Date();
    let age = today.getFullYear() - birth.getUTCFullYear();
    const monthDiff = today.getMonth() - birth.getUTCMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getUTCDate())) age -= 1;
    return age >= 0 && age < 150 ? age : null;
};

const Avatar = ({ person, testId }) => {
    const src = person?.image ? getFileURL(person.image) : '';
    if (src) {
        return (
            <img
                data-testid={testId}
                src={src}
                alt={fullName(person)}
                className="w-9 h-9 rounded-full object-cover flex-shrink-0 border border-[#C7D6D5]"
            />
        );
    }
    return (
        <span
            data-testid={testId}
            className="w-9 h-9 rounded-full flex-shrink-0 flex items-center justify-center bg-gradient-to-br from-[#4ABDBD] to-[#2D8A8A] text-white text-xs font-bold"
        >
            {initialsOf(person)}
        </span>
    );
};

export default function HouseholdPicker({
    household,
    tPrefix,
    testId = 'household',
    selectedId = null,
    onSelect,
    onAddMember,
    adding = false,
    addError = '',
    disabled = false,
    relationships,
    footer = null,
}) {
    const { t } = useTranslation();
    const [mode, setMode] = useState('pick');   // 'pick' | 'add'
    const EMPTY_MEMBER = { name: '', surname: '', dob: '', gender: '', relation: '', consentConfirmed: false };
    const [member, setMember] = useState(EMPTY_MEMBER);
    const [memberErrors, setMemberErrors] = useState({});

    const key = (k) => `${tPrefix}.${k}`;
    const relationList = (Array.isArray(relationships) && relationships.length)
        ? relationships
        : HOUSEHOLD_RELATIONSHIPS;

    // The primary is always first — it is the account the contact belongs to,
    // and the row the desk means nine times out of ten.
    const rows = useMemo(() => {
        const list = [];
        if (household?.primary?.patientId) list.push({ ...household.primary, accountType: 'primary' });
        (household?.members || []).forEach((m) => {
            if (m?.patientId) list.push({ ...m, accountType: m.accountType || 'secondary' });
        });
        return list;
    }, [household]);

    if (!household || !rows.length) return null;

    const householdName = fullName(household.primary) || household.contact || '';

    const pick = (person) => {
        setMode('pick');
        setMemberErrors({});
        if (typeof onSelect === 'function') onSelect(person);
    };

    const startAdd = () => {
        setMode('add');
        setMemberErrors({});
        // Picking the "new member" row means nobody existing is chosen any
        // more: without this the booking would still carry the last pick.
        if (typeof onSelect === 'function') onSelect(null);
    };

    const setField = (k, v) => {
        setMember((m) => ({ ...m, [k]: v }));
        setMemberErrors((e) => ({ ...e, [k]: undefined }));
    };

    // Which relations the server will ask for evidence of consent on.
    const needsConsent = CONSENT_REQUIRED.has(member.relation);

    const validate = () => {
        const e = {};
        if (!member.name.trim()) e.name = t(key('name_required'), 'First name is required');
        if (!member.surname.trim()) e.surname = t(key('surname_required'), 'Surname is required');
        if (!member.dob) e.dob = t(key('dob_required'), 'Date of birth is required');
        else if (ageFromDob(member.dob) === null) e.dob = t(key('dob_invalid'), 'Enter a valid date of birth');
        if (!member.gender) e.gender = t(key('gender_required'), 'Gender is required');
        if (!member.relation) e.relation = t(key('relation_required'), 'Choose the relationship');
        else if (!HOUSEHOLD_RELATIONSHIPS.includes(member.relation) && !relationList.includes(member.relation)) {
            e.relation = t(key('relation_required'), 'Choose the relationship');
        }
        if (needsConsent && !member.consentConfirmed) {
            e.consentConfirmed = t('family.consent_required', 'Please confirm you have their permission');
        }
        setMemberErrors(e);
        return Object.keys(e).length === 0;
    };

    const submitMember = async () => {
        if (adding || disabled) return;
        if (!validate()) return;
        if (typeof onAddMember !== 'function') return;
        await onAddMember({
            name: member.name.trim(),
            surname: member.surname.trim(),
            dob: member.dob,
            gender: member.gender,
            relation: member.relation,
            // Sent only where it means something. A blanket `false` on a Son
            // would look like a consent that was asked for and refused.
            ...(needsConsent ? { consentConfirmed: true } : {}),
        });
    };

    const rowClass = (active) => `w-full flex items-center gap-3 px-3 py-2.5 rounded-xl border text-left transition ${active
        ? 'border-[#4ABDBD] bg-[#4ABDBD]/5'
        : 'border-gray-200 bg-white hover:border-[#9DB3B1]'
        }`;

    return (
        <div
            data-testid={`${testId}-card`}
            className="sm:col-span-2 lg:col-span-3 mt-2 rounded-2xl border border-[#C7D6D5] bg-[#F7FBFB] p-4"
        >
            <div className="flex items-start gap-2 mb-1">
                <Users className="text-[#2D8A8A] w-4 h-4 mt-0.5 flex-shrink-0" />
                <p data-testid={`${testId}-title`} className="text-sm font-semibold text-[#2D8A8A]">
                    {t(key('title'), { name: householdName, defaultValue: `This contact belongs to ${householdName}'s household` })}
                </p>
            </div>
            <p data-testid={`${testId}-subtitle`} className="text-xs text-gray-500 mb-3 ml-6">
                {t(key('subtitle'), 'Choose who this is for, or add a new family member.')}
            </p>

            <div className="space-y-2" role="radiogroup" aria-label={t(key('title'), { name: householdName, defaultValue: 'Household' })}>
                {rows.map((person) => {
                    const active = selectedId === person.patientId && mode === 'pick';
                    const age = ageFromDob(person.dob);
                    return (
                        <label
                            key={person.patientId}
                            data-testid={`${testId}-row-${person.patientId}`}
                            className={`${rowClass(active)} cursor-pointer`}
                        >
                            <input
                                data-testid={`${testId}-radio-${person.patientId}`}
                                type="radio"
                                name={`${testId}-choice`}
                                value={person.patientId}
                                checked={active}
                                disabled={disabled || adding}
                                onChange={() => pick(person)}
                                className="w-4 h-4 text-[#4ABDBD] focus:ring-[#4ABDBD] cursor-pointer flex-shrink-0"
                            />
                            <Avatar person={person} testId={`${testId}-avatar-${person.patientId}`} />
                            <span className="min-w-0 flex-1">
                                <span className="block text-sm font-semibold text-gray-800 truncate">
                                    {fullName(person) || t('common.patient', 'Patient')}
                                </span>
                                <span className="block text-xs text-gray-500 truncate">
                                    {[
                                        age === null ? null : t(key('years_old'), { age, defaultValue: `${age} yrs` }),
                                        person.gender || null,
                                        person.accountType === 'primary' ? null : (person.relation || null),
                                        hospitalPatientCode(person),
                                        // A member with their own sign-in is
                                        // reachable directly; one without is
                                        // only ever reached through the primary.
                                        person.accountType !== 'primary' && person.hasLogin ? t(key('own_login'), 'own sign-in') : null,
                                    ].filter(Boolean).join(' · ')}
                                </span>
                            </span>
                            <span
                                data-testid={`${testId}-tag-${person.patientId}`}
                                className={`flex-shrink-0 text-[10px] font-bold uppercase tracking-wide px-2 py-1 rounded-full ${person.accountType === 'primary'
                                    ? 'bg-[#2D8A8A] text-white'
                                    : 'bg-[#C7D6D5] text-[#2D8A8A]'
                                    }`}
                            >
                                {person.accountType === 'primary'
                                    ? t(key('primary_tag'), 'Primary')
                                    : t(key('member_tag'), 'Family member')}
                            </span>
                        </label>
                    );
                })}

                {household.canAddMember && (
                    <label
                        data-testid={`${testId}-row-new`}
                        className={`${rowClass(mode === 'add')} cursor-pointer`}
                    >
                        <input
                            data-testid={`${testId}-radio-new`}
                            type="radio"
                            name={`${testId}-choice`}
                            value={ADD_NEW_MEMBER}
                            checked={mode === 'add'}
                            disabled={disabled || adding}
                            onChange={startAdd}
                            className="w-4 h-4 text-[#4ABDBD] focus:ring-[#4ABDBD] cursor-pointer flex-shrink-0"
                        />
                        <span className="w-9 h-9 rounded-full flex-shrink-0 flex items-center justify-center bg-white border border-dashed border-[#4ABDBD]">
                            <UserPlus className="w-4 h-4 text-[#2D8A8A]" />
                        </span>
                        <span className="text-sm font-semibold text-[#2D8A8A]">
                            {t(key('add_member'), 'Add as a new family member')}
                        </span>
                    </label>
                )}
            </div>

            {mode === 'add' && household.canAddMember && (
                <div data-testid={`${testId}-member-form`} className="mt-3 rounded-xl border border-[#C7D6D5] bg-white p-3">
                    {/* No e-mail, no mobile: the contact is the primary's, which
                        is what makes this person a household member at all. */}
                    <p className="text-xs text-gray-500 mb-3">
                        {t(key('member_form_hint'), { contact: household.contact || '', defaultValue: `The new member is reached on ${household.contact || 'this contact'} — no separate e-mail or mobile is needed.` })}
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <label className="block text-xs font-semibold text-gray-700 mb-1" htmlFor={`${testId}-member-name`}>
                                {t(key('name'), 'First name')} <span className="text-red-500">*</span>
                            </label>
                            <input
                                id={`${testId}-member-name`}
                                data-testid={`${testId}-member-name`}
                                type="text"
                                value={member.name}
                                maxLength={20}
                                disabled={disabled || adding}
                                onChange={(e) => setField('name', e.target.value)}
                                className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] ${memberErrors.name ? 'border-red-400' : 'border-gray-300'}`}
                            />
                            {memberErrors.name && <p data-testid={`${testId}-member-name-error`} className="text-xs text-red-500 mt-1">{memberErrors.name}</p>}
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-gray-700 mb-1" htmlFor={`${testId}-member-surname`}>
                                {t(key('surname'), 'Surname')} <span className="text-red-500">*</span>
                            </label>
                            <input
                                id={`${testId}-member-surname`}
                                data-testid={`${testId}-member-surname`}
                                type="text"
                                value={member.surname}
                                maxLength={20}
                                disabled={disabled || adding}
                                onChange={(e) => setField('surname', e.target.value)}
                                className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] ${memberErrors.surname ? 'border-red-400' : 'border-gray-300'}`}
                            />
                            {memberErrors.surname && <p data-testid={`${testId}-member-surname-error`} className="text-xs text-red-500 mt-1">{memberErrors.surname}</p>}
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-gray-700 mb-1" htmlFor={`${testId}-member-dob`}>
                                {t(key('dob'), 'Date of birth')} <span className="text-red-500">*</span>
                            </label>
                            <input
                                id={`${testId}-member-dob`}
                                data-testid={`${testId}-member-dob`}
                                type="date"
                                value={member.dob}
                                min="1900-01-01"
                                max={new Date().toISOString().split('T')[0]}
                                disabled={disabled || adding}
                                onChange={(e) => setField('dob', e.target.value)}
                                className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] ${memberErrors.dob ? 'border-red-400' : 'border-gray-300'}`}
                            />
                            {memberErrors.dob && <p data-testid={`${testId}-member-dob-error`} className="text-xs text-red-500 mt-1">{memberErrors.dob}</p>}
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-gray-700 mb-1" htmlFor={`${testId}-member-gender`}>
                                {t(key('gender'), 'Gender')} <span className="text-red-500">*</span>
                            </label>
                            <select
                                id={`${testId}-member-gender`}
                                data-testid={`${testId}-member-gender`}
                                value={member.gender}
                                disabled={disabled || adding}
                                onChange={(e) => setField('gender', e.target.value)}
                                className={`w-full border rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] ${memberErrors.gender ? 'border-red-400' : 'border-gray-300'}`}
                            >
                                <option value="">{t(key('select_gender'), 'Select gender')}</option>
                                <option value="male">{t('common.gender.male', 'Male')}</option>
                                <option value="female">{t('common.gender.female', 'Female')}</option>
                                <option value="Not_Communicated">{t('common.gender.notCommunicated', 'Not Communicated')}</option>
                                <option value="other">{t('common.gender.other', 'Other')}</option>
                            </select>
                            {memberErrors.gender && <p data-testid={`${testId}-member-gender-error`} className="text-xs text-red-500 mt-1">{memberErrors.gender}</p>}
                        </div>
                        <div className="sm:col-span-2">
                            <label className="block text-xs font-semibold text-gray-700 mb-1" htmlFor={`${testId}-member-relation`}>
                                {t(key('relation'), 'Relationship to the contact')} <span className="text-red-500">*</span>
                            </label>
                            <select
                                id={`${testId}-member-relation`}
                                data-testid={`${testId}-member-relation`}
                                value={member.relation}
                                disabled={disabled || adding}
                                onChange={(e) => setField('relation', e.target.value)}
                                className={`w-full border rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] ${memberErrors.relation ? 'border-red-400' : 'border-gray-300'}`}
                            >
                                <option value="">{t(key('select_relation'), 'Select relationship')}</option>
                                {relationList.map((r) => (
                                    <option key={r} value={r}>{t(`family.rel.${String(r).toLowerCase()}`, r)}</option>
                                ))}
                            </select>
                            {memberErrors.relation && <p data-testid={`${testId}-member-relation-error`} className="text-xs text-red-500 mt-1">{memberErrors.relation}</p>}
                        </div>
                    </div>

                    {needsConsent && (
                        <label className="mt-3 flex items-start gap-2.5 rounded-xl bg-amber-50 border border-amber-200 px-3 py-2.5 cursor-pointer">
                            <input
                                type="checkbox"
                                data-testid={`${testId}-member-consent`}
                                checked={member.consentConfirmed}
                                onChange={(e) => setField('consentConfirmed', e.target.checked)}
                                disabled={disabled || adding}
                                className="mt-0.5 accent-[#4ABDBD] cursor-pointer"
                            />
                            <span className="text-[11px] leading-relaxed text-amber-900">
                                {t('family.consent_text', 'I confirm I have this person’s permission to manage their health records on their behalf.')}
                            </span>
                        </label>
                    )}
                    {memberErrors.consentConfirmed && (
                        <p data-testid={`${testId}-member-consent-error`} className="text-xs text-red-500 mt-1">{memberErrors.consentConfirmed}</p>
                    )}

                    {addError && (
                        <p data-testid={`${testId}-add-error`} className="text-xs text-red-500 mt-3">{addError}</p>
                    )}

                    <div className="flex items-center gap-2 mt-3">
                        <button
                            type="button"
                            data-testid={`${testId}-member-submit`}
                            onClick={submitMember}
                            disabled={disabled || adding}
                            className="text-xs px-3 py-2 rounded-lg bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white font-semibold hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
                        >
                            {adding ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                            {adding ? t(key('adding'), 'Adding…') : t(key('save_member'), 'Add member')}
                        </button>
                        <button
                            type="button"
                            data-testid={`${testId}-member-cancel`}
                            onClick={() => { setMode('pick'); setMember(EMPTY_MEMBER); setMemberErrors({}); }}
                            disabled={adding}
                            className="text-xs px-3 py-2 rounded-lg border border-gray-300 text-gray-600 font-semibold hover:bg-gray-50 disabled:opacity-50"
                        >
                            {t(key('cancel'), 'Cancel')}
                        </button>
                    </div>
                </div>
            )}

            {footer}
        </div>
    );
}
